import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import Navbar from "@/components/Navbar";

const gsaDestructionScenarios = [
  {
    title: "Outdated Space Planning Guidelines",
    description: "Remove pre-pandemic office density standards that no longer reflect hybrid work realities",
    impact: "High",
    urgency: "Medium",
    contentType: "Policy Documents"
  },
  {
    title: "Obsolete Security Protocols",
    description: "Eliminate deprecated ISC guidelines that conflict with modern cybersecurity frameworks",
    impact: "Critical",
    urgency: "High", 
    contentType: "Security Standards"
  },
  {
    title: "Legacy Procurement Templates",
    description: "Remove outdated GSA Schedule templates that don't support sustainable acquisition goals",
    impact: "Medium",
    urgency: "Medium",
    contentType: "Procurement Documents"
  },
  {
    title: "Incorrect Energy Modeling Data",
    description: "Delete building performance baselines using outdated ASHRAE standards",
    impact: "High",
    urgency: "Low",
    contentType: "Technical Specifications"
  },
  {
    title: "Deprecated Accessibility Guidelines",
    description: "Remove superseded ADA compliance checklists that don't align with current Section 508 requirements",
    impact: "Critical",
    urgency: "High",
    contentType: "Compliance Standards"
  },
  {
    title: "Inaccurate Cost Estimation Models",
    description: "Eliminate pre-inflation construction cost models causing budget overruns",
    impact: "High",
    urgency: "Medium",
    contentType: "Financial Templates"
  }
];

const GsaCallForDestruction = () => {
  const navigate = useNavigate();
  const [selectedScenario, setSelectedScenario] = useState<number | null>(null);

  const handleScenarioSelect = (index: number) => {
    setSelectedScenario(index);
  };

  const handleProceed = () => {
    if (selectedScenario !== null) {
      navigate("/gsa-call-for-destruction/bounty", {
        state: {
          selectedScenario: gsaDestructionScenarios[selectedScenario]
        }
      });
    }
  };

  const getImpactColor = (impact: string) => {
    switch (impact) {
      case 'Critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'High': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getUrgencyColor = (urgency: string) => {
    switch (urgency) {
      case 'High': return 'bg-red-100 text-red-800 border-red-200';
      case 'Medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'Low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-8">
          <img 
            src="/lovable-uploads/bb3f5ac9-6c4b-494d-9a60-86ee7a3feb77.png" 
            alt="iSPAI Logo" 
            className="h-16 mx-auto mb-6 cursor-pointer"
            onClick={() => navigate("/gsa-channel")}
          />
          <h1 className="text-3xl font-bold mb-4">GSA Call for Destruction Demo</h1>
          <p className="text-lg text-gray-600 mb-8">
            Select a scenario to demonstrate how iSPAI helps remove outdated or harmful content from GSA knowledge bases
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {gsaDestructionScenarios.map((scenario, index) => (
            <Card 
              key={index}
              className={`cursor-pointer transition-all duration-200 hover:shadow-lg ${
                selectedScenario === index 
                  ? 'border-2 border-red-500 bg-red-50' 
                  : 'border border-gray-200 hover:border-red-300'
              }`}
              onClick={() => handleScenarioSelect(index)}
            >
              <CardHeader>
                <div className="flex justify-between items-start mb-2">
                  <CardTitle className="text-lg">
                    {scenario.title}
                  </CardTitle>
                  <span className="text-xs text-gray-500">
                    {scenario.contentType}
                  </span>
                </div>
                <div className="flex gap-2">
                  <span className={`px-2 py-1 text-xs rounded-full border ${getImpactColor(scenario.impact)}`}>
                    Impact: {scenario.impact}
                  </span>
                  <span className={`px-2 py-1 text-xs rounded-full border ${getUrgencyColor(scenario.urgency)}`}>
                    Urgency: {scenario.urgency}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-700">
                  {scenario.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Info Panel */}
        <Card className="mb-8 border-blue-200 bg-blue-50">
          <CardContent className="pt-6">
            <h3 className="text-lg font-semibold text-blue-800 mb-2">GSA Knowledge Curation</h3>
            <p className="text-blue-700 text-sm">
              Call for Destruction helps maintain accuracy in GSA knowledge bases by identifying and removing 
              outdated, incorrect, or harmful content. This ensures federal employees have access to current, 
              compliant information for decision-making.
            </p>
          </CardContent>
        </Card>

        <div className="flex justify-center space-x-4">
          <Button 
            variant="outline" 
            onClick={() => navigate("/gsa-channel")}
            className="px-8 py-2"
          >
            Back to GSA Channel
          </Button>
          
          <Button 
            onClick={handleProceed}
            disabled={selectedScenario === null}
            className="bg-[#14213d] hover:bg-[#0d1829] text-white px-8 py-2"
          >
            Create Destruction Request
          </Button>
        </div>
      </main>
    </div>
  );
};

export default GsaCallForDestruction;